import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Typography,
  styled,
  useTheme,
  RadioGroup,
  Radio,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import NoSleep from 'nosleep.js';
import { getWaveBlob } from 'webm-to-wav-converter';
import SettingsIcon from '@mui/icons-material/Settings';
import { speechToTextSuggestion } from '../../service/api/notesApi';
import { chatSlimScrollbarStyles, slimScrollbarStyles } from '../../utils/styles';
import { useAppSelector } from '../../redux/hooks';
import {
  IProcessedAIResponse,
  appendClickItem,
  clearClickedAndRemovedItems,
  saveNoteRecording,
  addTextNoteRequest,
  getNoteRecordings,
  resetNoteRecordings,
  changeInputDevice,
} from '../../redux/note/noteSlice';
import useSpeechToTextSocket from '../../service/websocket/useSpeechToTextSocket';
import useAudioStreamer from '../../hooks/useAudioStreamer';
import StyledButton from '../Common/StyledButton';
import { toastStyles } from '../../utils/utils';
import RecordingOverview from './RecordingOverview';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import SpeechToTextTabs from './Tabs';
import Recordings from './Recordings';
import LoadingButton from '../Common/LoadingButton';
import { concatenateTranscriptions, processResponse } from '../../utils/notes';
import MicrophoneAccessModal from './MicrophoneAccessModal';
import CustomModal from '../Common/CustomModal';

const StopRecordingButton = styled(Button)(() => ({
  borderRadius: '15px',
  border: '1.5px solid var(--Alert-Sunset-Orange, #FF9A9A)',
  '& span': {
    background: 'linear-gradient(175deg, #FF9A9A -9.81%, #FF5858 96.32%))',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    fontFamily: 'Satoshi',
    fontSize: '17px',
    fontWeight: 700,
    lineHeight: '22px',
  },
}));

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: '5px',
  background: 'rgba(124, 174, 190, 0.15)',
  border: 'none',
  padding: '12px',
  alignItems: 'center',
}));

interface ISuggestionsProps {
  appendSuggestions: (_suggestion: string) => void;
}

type TAudioDevice = Omit<MediaDeviceInfo, 'toJSON'>;

const mimeType = 'audio/webm';

function SpeechToText({ appendSuggestions }: ISuggestionsProps) {
  const dispatch = useDispatch();
  const { id: clientId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [suggestNotes, setSuggestNotes] = useState<IProcessedAIResponse[]>([]);
  const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
  const [clientNotes, setClientNotes] = useState('');
  const [completedTranscription, setCompletedTranscription] = useState<string>('');
  const [liveTranscription, setLiveTranscription] = useState<string>('');
  const [isCurrentlyRecording, setIsCurrentlyRecording] = useState(false);
  const [isRecordOverviewOpen, setRecordOverviewOpen] = useState(false);
  const [maskedSummary, setMaskedSummary] = useState<string>('');
  const [rowSummary, setRowSummary] = useState<string>('');
  const buttonStartIcon = '/assets/icons/microphone-small.svg';
  const [selectedText, setSelectedText] = useState<string>('');
  const noteAssistEnabled = useAppSelector(
    (state) => state.client.privacySettings.noteAssistEnabled
  );
  const [shouldRecord, setShouldRecord] = useState(false);
  const [shouldRecordAgain, setShouldRecordAgain] = useState(false);
  const [value, setValue] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const coachAISettings = useAppSelector((state) => state.coach.coachAISettings);
  const theme = useTheme();
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const {
    selectedNoteId: noteId,
    saveNoteRecordingLoading,
    noteRecordings,
    getNoteRecordingsLoading,
    inputDeviceId,
  } = useAppSelector((state) => state.note);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [devices, setDevices] = useState<TAudioDevice[]>([]);
  const [isMicSelectOpen, setIsMicSelectOpen] = useState(false);

  // storing clicked and removed items
  const clickedItems = useAppSelector((state) => state.note.clickedItems);
  const removedItems = useAppSelector((state) => state.note.removedItems);

  // NoSleep to keep the screen awake while recording
  const noSleep = new NoSleep();

  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const clientSpeechText = useAppSelector((state) => state.note.clientSpeechText);
  const fullTranscription = `${completedTranscription} ${liveTranscription}`;
  const [isMicrophoneModalOpen, setIsMicrophoneModalOpen] = useState(false);

  useEffect(() => {
    const newSelectedText = clickedItems.concat(removedItems).join(',');
    setSelectedText(newSelectedText);
  }, [clickedItems, removedItems]);

  // clears everything when component unmounts
  useEffect(
    () => () => {
      dispatch(clearClickedAndRemovedItems());
    },
    []
  );

  const socket = useSpeechToTextSocket();
  const {
    initAudioStreamer,
    initRecording: initAudioStreamRecording,
    stopRecording: stopAudioStreamRecording,
  } = useAudioStreamer(socket);

  // ignoreTimestampCheck= true  if there's force call
  const getSuggestionsRequest = async (notes: string) => {
    if (!clientId) return;
    try {
      setFetchingSuggestions(true);
      let response: {
        content: string;
        maskedSummary: string;
        summaryMessage: string;
      };
      if (noteId) {
        const pastTranscriptions = concatenateTranscriptions(noteRecordings, noteId);
        response = await speechToTextSuggestion(`${pastTranscriptions}. ${notes}`, selectedText);
      } else {
        response = await speechToTextSuggestion(notes, selectedText);
      }
      setSuggestNotes(processResponse(response.content));
      setMaskedSummary(response.maskedSummary);
      setRowSummary(response.summaryMessage);
      setFetchingSuggestions(false);
    } catch (dataSendError: unknown) {
      setFetchingSuggestions(false);
      toast.error(t('notes.summarizeErrorMessage'), toastStyles.error);
    }
  };

  // debounce to avoid multiple calls to getSuggestionsRequest and to avoid calling on every transcription change
  const getRealtimeSuggestionSummaries = useCallback(
    debounce((notes: string) => {
      if (fetchingSuggestions) {
        return;
      }

      getSuggestionsRequest(notes);
    }, 60000),
    [fetchingSuggestions]
  );

  const removeSuggestion = (suggestionId: string) => {
    setSuggestNotes((prev) => {
      const suggestionTopics = [...prev];

      for (let i = 0; i < suggestionTopics.length; i += 1) {
        const item = suggestionTopics[i];
        item.data = item.data.filter((item1) => item1.id !== suggestionId);
        suggestionTopics[i] = item;
      }
      return suggestionTopics;
    });
  };

  const appendClickHandler = (event: React.MouseEvent, suggestionId: string, content: string) => {
    event.stopPropagation();
    appendSuggestions(content);
    // add to clicked items
    dispatch(appendClickItem(content));
    // remove from suggestions
    removeSuggestion(suggestionId);
  };

  const removeHandler = (event: React.MouseEvent, suggestionId: string) => {
    event.stopPropagation();
    removeSuggestion(suggestionId);
  };

  const setSelectedDevice = (id: string = 'default', isUserAction = false) => {
    const audio = devices.find((d) => d.deviceId === id);
    let deviceName = 'unknown';
    let deviceId = 'default';
    if (audio?.label && audio?.deviceId) {
      deviceName = audio.label;
      deviceId = audio.deviceId;
    } else if (id === 'default') {
      deviceName = 'Default';
      deviceId = 'default';
    }
    dispatch(changeInputDevice(deviceId));
    if (isUserAction) {
      toast.success(t('notes.microphoneChangedSuccess', { name: deviceName }), toastStyles.success);
    }
    setIsMicSelectOpen(false);
  };

  const updateInputDevices = () => {
    navigator.mediaDevices.enumerateDevices().then((deviceList) => {
      const audioDevices: TAudioDevice[] = deviceList.filter(
        (d) => d.kind === 'audioinput' && d.deviceId
      );
      if (!audioDevices.length) {
        audioDevices.push({
          deviceId: 'default',
          groupId: '',
          kind: 'audioinput',
          label: 'Default',
        });
      }
      setDevices(audioDevices);
    });
  };

  // update on load
  useEffect(() => {
    updateInputDevices();
  }, []);

  const startRecording = () => {
    // keep the screen awake while recording
    noSleep.enable();
    setShouldRecord(true);
    setShouldRecordAgain(false);
    initAudioStreamer();

    if (!isCurrentlyRecording) {
      setCompletedTranscription('');
      setLiveTranscription('');
    }
    setIsCurrentlyRecording(true);

    initAudioStreamRecording(
      (data: string) => {
        if (data.trim() === '') return;
        setLiveTranscription('');
        setCompletedTranscription((prev) => `${prev}${data}. `);
      },
      (liveTranscriptionData: string) => {
        if (liveTranscriptionData.trim() === '') return;
        setLiveTranscription(liveTranscriptionData);
      },
      () => {
        setShouldRecord(true);
      }
    );

    navigator.mediaDevices
      .getUserMedia({ audio: { deviceId: inputDeviceId ?? 'default' } })
      .then((stream) => {
        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;
        const chunks: Blob[] = [];
        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.start();
      })
      .catch((error) => {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          // Permission was denied
          setIsMicrophoneModalOpen(true);
          setShouldRecord(false);
        } else {
          // Handle other errors
          toast.error(error, toastStyles.error);
        }
      });
  };

  const stopRecording = async () => {
    // disable the screen awake while recording
    noSleep.disable();
    setIsCurrentlyRecording(false);
    setIsConverting(true);
    stopAudioStreamRecording();
    getRealtimeSuggestionSummaries?.cancel();
    getRealtimeSuggestionSummaries?.flush();
    if (completedTranscription.trim().length > 0) {
      getSuggestionsRequest(completedTranscription);
    }

    // setShouldRecord(false);
    setShouldRecordAgain(true);

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      const audioChunks: Blob[] = [];
      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        audioChunks.push(event.data);
      });

      mediaRecorderRef.current.addEventListener('stop', async () => {
        try {
          const audioBlob = new Blob(audioChunks, { type: mimeType });
          const waveBlob = await getWaveBlob(audioBlob, false, { sampleRate: 16000 });
          const audioFile = new File([waveBlob], `${Date.now().toString()}.wav`, {
            type: 'audio/wav',
          });
          if (!clientId || !audioFile || !fullTranscription.trim()) return;
          if (noteId) {
            dispatch(
              saveNoteRecording({
                clientId,
                coachId,
                noteId,
                transcription: fullTranscription,
                contentType: 'audio/wav',
                file: audioFile,
                recording: audioFile.name,
                suggestions: suggestNotes
                  .map((suggestion) =>
                    suggestion.data.map((data) => ({
                      id: data.id,
                      suggestion: data.value,
                      type: suggestion.title,
                    }))
                  )
                  .flat(),
              })
            );
          } else {
            dispatch(
              addTextNoteRequest({
                tempId: '',
                clientId,
                coachId,
                text: '',
                html: '<p></p>',
                transcription: fullTranscription,
                contentType: 'audio/wav',
                file: audioFile,
                recording: audioFile.name,
              })
            );
          }
        } finally {
          setIsConverting(false);
          mediaRecorderRef.current = null;
        }
      });
    }
  };

  // Calls the function to get suggestions when there's a change in completed transcription.
  useEffect(() => {
    if (!completedTranscription || completedTranscription.trim().length === 0) {
      return;
    }

    // If there's no any suggestNotes (Initial transcription) and if there's no any ongoing fetch
    // suggestion calls, directly get the suggestions without waiting for debounce. But need to wait
    // at least we have 100 characters in the completed transcription to avoid small initial chunks
    // such as welcome
    if (suggestNotes.length === 0 && !fetchingSuggestions) {
      if (completedTranscription.trim().length >= 100) {
        getSuggestionsRequest(completedTranscription);
      }
    }

    if (fetchingSuggestions) {
      return;
    }

    getRealtimeSuggestionSummaries(completedTranscription);
  }, [completedTranscription]);

  // suggestions for saved notes
  useEffect(() => {
    const initialNotes = clientSpeechText.find((item) => item.clientId === clientId)?.speechText;
    setClientNotes(initialNotes?.trim() || '');
    return () => {
      if (!shouldRecord && isCurrentlyRecording) {
        stopRecording();
      }
    };
  }, [clientSpeechText]);

  // stop recording and clears everything when component unmounts
  useEffect(
    () => () => {
      if (shouldRecord && isCurrentlyRecording) {
        stopRecording();
      }
    },
    [isCurrentlyRecording]
  );

  useEffect(() => {
    if (!clientId || !coachId) return;
    if (noteId) {
      dispatch(
        getNoteRecordings({
          clientId,
          coachId,
          noteId,
        })
      );
    } else {
      dispatch(resetNoteRecordings());
    }
  }, [clientId, coachId, noteId]);

  const handleRetry = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((_stream) => {
        // If successful, close the modal and start recording
        setIsMicrophoneModalOpen(false);
        setShouldRecord(false);
      })
      .catch((error) => {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setIsMicrophoneModalOpen(true);
        } else {
          toast.error(error, toastStyles.error);
        }
      });
  };

  const handleDismiss = () => {
    setIsMicrophoneModalOpen(false);
    setIsCurrentlyRecording(false);
    setShouldRecord(false);
  };

  useEffect(() => {
    if (!devices?.length) {
      setSelectedDevice('default');
    } else if (devices?.length === 1) {
      setSelectedDevice(devices[0].deviceId);
    } else if (!devices.some((d) => d.deviceId === inputDeviceId)) {
      setSelectedDevice('default');
    }
  }, [devices, inputDeviceId]);

  if (getNoteRecordingsLoading) {
    // Render loading screen while getNoteRecordingsLoading is true
    return (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        bgcolor="#fff"
        borderRadius="10px"
        boxShadow={theme.borderStyles.cardBorders}
      >
        <CircularProgress />
        <Typography variant="bodyRegular" color="primary" mt="20px">
          {t('notes.loadingRecordings')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      height="100%"
      borderRadius="10px"
      bgcolor="#fff"
      boxShadow={theme.borderStyles.cardBorders}
    >
      <Box
        width="100%"
        display="flex"
        alignItems="end"
        flexDirection="row"
        justifyContent="end"
        pr="5px"
        pt="5px"
        sx={{ cursor: 'pointer' }}
      >
        <Tooltip title="Select input device" enterDelay={1000}>
          <SettingsIcon
            fontSize="small"
            color="secondary"
            onClick={() => setIsMicSelectOpen(true)}
          />
        </Tooltip>
      </Box>
      <CustomModal isOpen={isMicrophoneModalOpen} xs={3.3}>
        <MicrophoneAccessModal handleDismiss={handleDismiss} handleRetry={handleRetry} />
      </CustomModal>
      {!shouldRecord && suggestNotes.length === 0 && noteRecordings.length === 0 && (
        <Box
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          gap="36px"
          p="24px"
        >
          <img src="/assets/icons/microphone.svg" alt="mic" />
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            justifyContent="center"
            gap="10px"
          >
            <Typography variant="title2Bold" textAlign="center" color="primary">
              {t('notes.StartRecordingSuggestNotes')}
            </Typography>
            <Typography variant="bodyRegular" textAlign="center" color="primary">
              {t('notes.dontSaveRecordings')}
            </Typography>
          </Box>
          <StyledButton
            onClick={startRecording}
            startIcon={buttonStartIcon}
            fullWidth
            paddingY={12}
            disabled={!noteAssistEnabled || !coachAISettings.isNoteTakingAIEnabled}
          >
            {t('notes.startRecording')}
          </StyledButton>
        </Box>
      )}
      {(clientNotes !== '' ||
        shouldRecord ||
        suggestNotes.length > 0 ||
        noteRecordings.length > 0) && (
        <Box height="100%" bgcolor="#fff" p="24px">
          {shouldRecord && !shouldRecordAgain && (
            <StopRecordingButton variant="outlined" onClick={stopRecording} fullWidth color="error">
              <img
                src="/assets/icons/stop-circle.svg"
                alt="mic"
                style={{
                  marginRight: '8px',
                  animation: 'appear-disappear 2s infinite',
                }}
              />
              <span>{t('notes.stopRecording')}</span>
            </StopRecordingButton>
          )}
          {(shouldRecordAgain && !saveNoteRecordingLoading && !isConverting) || !shouldRecord ? (
            <StyledButton
              onClick={startRecording}
              fullWidth
              startIcon={buttonStartIcon}
              paddingY={12}
            >
              {t('notes.recordAgain')}
            </StyledButton>
          ) : (
            shouldRecordAgain &&
            (saveNoteRecordingLoading || isConverting) &&
            fullTranscription.trim() && (
              <LoadingButton fullWidth paddingY={15}>
                {t('notes.savingRecording')}
              </LoadingButton>
            )
          )}
          {isCurrentlyRecording && (
            <Box
              sx={{
                height: '15%',
                marginTop: '8px',
                overflow: 'auto',
                ...slimScrollbarStyles,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="button"
                  textAlign="left"
                  textTransform="capitalize"
                  color="primary"
                  fontWeight={700}
                >
                  {t('notes.transcription')}
                </Typography>
                <Box
                  onClick={() => setRecordOverviewOpen(true)}
                  component="img"
                  src="/assets/icons/document.svg"
                  sx={{
                    cursor: 'pointer',
                    marginLeft: '8px',
                    height: '24px',
                    width: '24px',
                  }}
                />
              </Box>

              {fullTranscription && (
                <Box sx={{ marginTop: '8px' }}>
                  <Typography variant="bodyRegular" textAlign="left" color="primary">
                    {fullTranscription.length > 200
                      ? fullTranscription.substring(fullTranscription.length - 100)
                      : fullTranscription}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <SpeechToTextTabs value={value} setValue={setValue} />
          {value === 0 && (
            <>
              <Box
                display="flex"
                flexDirection="column"
                overflow="scroll"
                gap="20px"
                height={isCurrentlyRecording ? '60%' : '75%'}
                sx={{
                  ...chatSlimScrollbarStyles,
                  overflowX: 'hidden',
                }}
              >
                {suggestNotes.length > 0 &&
                  (checkPermissions(
                    UserPermissionType.MAGIC_SUGGESTIONS_MODULE_VIEW_SUGGESTIONS
                  ) ? (
                    suggestNotes.map((suggestionType) => (
                      <Box
                        key={suggestionType.title}
                        display="flex"
                        gap="10px"
                        flexDirection="column"
                      >
                        <Typography
                          variant="button"
                          textTransform="capitalize"
                          mx="5px"
                          fontWeight={700}
                          color="primary"
                        >
                          {suggestionType.title}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap="4px" px="5px">
                          {suggestionType.data.length > 0 ? (
                            suggestionType.data.map((suggestion) => (
                              <Box
                                key={suggestion.id}
                                sx={{
                                  cursor: 'pointer',
                                }}
                              >
                                <StyledBox
                                  onClick={(event) =>
                                    appendClickHandler(event, suggestion.id, suggestion.value)
                                  }
                                >
                                  <Typography
                                    key={suggestion.id}
                                    variant="bodyRegular"
                                    color="primary"
                                  >
                                    {suggestion.value}
                                  </Typography>
                                  <Box>
                                    <img
                                      src="/assets/icons/icon-park-outline-close.svg"
                                      alt="plus"
                                      role="presentation"
                                      style={{
                                        marginRight: '8px',
                                      }}
                                      onClick={(event) => removeHandler(event, suggestion.id)}
                                    />
                                  </Box>
                                </StyledBox>
                              </Box>
                            ))
                          ) : (
                            <Typography variant="bodyRegular" color="primary">
                              {t('notes.noSuggestions')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="bodyRegular" color="primary">
                      {t('notes.noPermissionToViewSuggestions')}
                    </Typography>
                  ))}
              </Box>

              <Box height="4px">
                {fetchingSuggestions && (
                  <LinearProgress
                    sx={{
                      width: '100%',
                      height: '4px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  />
                )}
              </Box>
            </>
          )}
          {value === 1 && <Recordings isCurrentlyRecording={isCurrentlyRecording} />}
        </Box>
      )}
      <RecordingOverview
        maskedRecording={maskedSummary}
        rowRecording={rowSummary}
        isOpen={isRecordOverviewOpen}
        closeModal={() => setRecordOverviewOpen(false)}
      />
      <CustomModal isOpen={isMicSelectOpen} width="40%">
        <RadioGroup value={inputDeviceId}>
          {devices.map((d) => (
            <FormControlLabel
              onClick={() => setSelectedDevice(d.deviceId, true)}
              key={d.deviceId}
              value={d.deviceId}
              label={d.label}
              control={<Radio />}
            />
          ))}
        </RadioGroup>
      </CustomModal>
    </Box>
  );
}

export default SpeechToText;
